import axios, { AxiosInstance, AxiosError } from "axios";

const API_BASE_URL = import.meta.env.VITE_API_URL || "/v2";

const clearAdminSession = () => {
  localStorage.removeItem("admin_token");
  localStorage.removeItem("admin_id_token");
  localStorage.removeItem("admin_refresh_token");
  window.location.href = "/admin/login";
};

class APIClient {
  private client: AxiosInstance;
  private refreshPromise: Promise<void> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor for adding auth token
    this.client.interceptors.request.use(
      (config) => {
        const isAdminEndpoint = config.url?.startsWith("/admin");
        const token = isAdminEndpoint
          ? localStorage.getItem("admin_token")
          : localStorage.getItem("auth_token");

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor — refresh admin token on 401, then retry once
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const config = error.config;
        const isAdminEndpoint = config?.url?.startsWith("/admin");

        if (error.response?.status === 401 && isAdminEndpoint && config) {
          // Don't retry the refresh endpoint itself
          if (config.url === "/admin/refresh") {
            clearAdminSession();
            return Promise.reject(error);
          }

          // Deduplicate concurrent refresh attempts
          if (!this.refreshPromise) {
            this.refreshPromise = this.tryRefresh().finally(() => {
              this.refreshPromise = null;
            });
          }

          try {
            await this.refreshPromise;
            // Retry original request with new token
            config.headers.Authorization = `Bearer ${localStorage.getItem("admin_token")}`;
            return this.client(config);
          } catch {
            clearAdminSession();
            return Promise.reject(error);
          }
        }

        if (error.response?.status === 401 && !isAdminEndpoint) {
          localStorage.removeItem("auth_token");
          window.location.href = "/";
        }

        return Promise.reject(error);
      },
    );
  }

  private async tryRefresh(): Promise<void> {
    const refreshToken = localStorage.getItem("admin_refresh_token");
    if (!refreshToken) throw new Error("No refresh token");

    const response = await this.client.post("/admin/refresh", { refreshToken });
    localStorage.setItem("admin_token", response.data.accessToken);
    localStorage.setItem("admin_id_token", response.data.idToken);
  }

  get instance() {
    return this.client;
  }
}

export const apiClient = new APIClient().instance;
